<template>
  <div
    class="video-wrapper"
    :class="{hover: isHovering}"
    @mouseover="isHovering = true"
    @mouseout="isHovering = false"
  >
    <!-- Share overlay -->
    <share-overlay
      v-if="displayShareOverlay"
      :url="share.url"
      aria-hidden="true"
      tabindex="-1"
      @close="showShare"
    >
      <template #share-overlay-content>
        <p>{{ share.label }}</p>
        <div layout="row">
          <a
            class="share-download-link"
            :content-download-url="share.url"
            content-download-type="video"
            target="_blank"
            :href="'https://www.linkedin.com/shareArticle?url=' + share.url"
          >
            <img
              class="icon"
              src="/images/linkedin.svg"
              aria-hidden="true"
              alt=""
            >
          </a>
          <a
            class="share-download-link"
            :content-download-url="share.url"
            content-download-type="video"
            target="_blank"
            :href="'https://www.twitter.com/intent/tweet?url=' + share.url"
          >
            <img
              class="icon"
              src="/images/twitter.svg"
              aria-hidden="true"
              alt=""
            >
          </a>
          <a
            class="share-download-link"
            :content-download-url="share.url"
            content-download-type="video"
            target="_blank"
            :href="'https://www.facebook.com/sharer/sharer.php?u=' + share.url"
          >
            <img
              class="icon"
              src="/images/facebook.svg"
              aria-hidden="true"
              alt=""
            >
          </a>
          <a
            class="share-download-link"
            :content-download-url="share.url"
            content-download-type="video"
            :href="'mailto:?Subject=' + share.emailSubject + '&amp;body=' + share.url"
          >
            <img
              class="icon"
              src="/images/email.svg"
              aria-hidden="true"
              alt=""
            >
          </a>
        </div>
        <copy-to-clipboard :text="share.url">
          <template #click-to-copy>
            <a
              class="button share-download-link"
              :content-download-url="share.url"
              content-download-type="video"
            >{{ share.buttonLabel }}</a>
          </template>
          <template #result>
            <p>{{ share.buttonResultText }}</p>
          </template>
        </copy-to-clipboard>
      </template>
    </share-overlay>

    <div
      v-if="!disableShare && !displayShareOverlay && share && share.url.length"
      class="gradientback"
      aria-hidden="true"
      tabindex="-1"
    >
      <a
        class="button button--header vt-video-share-button"
        @click="showShare"
      >{{ share.label }}</a>
    </div>

    <div
      v-definesize="autoSize"
      class="vt-video"
    >
      <div
        v-if="!autoplay"
        :class="['vt-video__poster lazyload', { 'vt-video__poster--active' : playing }]"
        :data-bgset="`${poster}?mw=800 [(max-width: 767px)] | ${poster}?mw=1280`"
      >
        <a
          class="button button--play"
          tabindex="0"
          @click="changeStatus"
          @keyup.enter="changeStatus"
        >
          <span class="sr-only">{{ srLabel }}</span>
          <svg>
            <use xlink:href="#icon-play" />
          </svg>
        </a>
      </div>
      <!-- Video -->
      <video
        :id="id"
        ref="video"
        preload="metadata"
        webkit-playsinline="true"
        playsinline="true"
        width="100%"
        height="100%"
        :poster="poster"
        frameborder="0"
        allow="autoplay"
        controls
        controlsList="nodownload"
        aria-hidden="true"
        :tabindex="playing ? 0:-1"
      >

        <source
          :src="data"
          type="video/mp4"
        >
        Your browser does not support the video tag.
      </video>
    </div>
  </div>
</template>

<script>
import ShareOverlay from '@molecules/share-overlay/share-overlay.vue'
import emitter from '../../../emitter'

export default {
  name: 'VideoLocal',
  components: {
    ShareOverlay
  },
  props: {
    data: {
      type: String,
      default: null,
      required: true
    },
    id: {
      type: String
    },
    autoplay: {
      type: Boolean
    },
    srLabel: {
      type: String,
      default: 'Video'
    },
    poster: {
      type: String,
      default: '/'
    },
    disableShare: {
      type: Boolean,
      default: false
    },
    share: {
      type: Object,
      defult: null
    },
    autoSize: {
      type: Boolean
    }
  },
  data() {
    return {
      playing: false,
      player: null,
      displayShareOverlay: false,
      isHovering: false
    }
  },
  watch: {
    playing(isPlaying) {
      if (isPlaying) {
        this.player.play()
      } else {
        this.player.pause()
      }
    }
  },
  created() {
    emitter.$on('vt-change-video', (payload) => {
      if (payload.id !== this.id) return

      if (payload.command === 'stop') {
        if (!this.playing) return

        this.playing = false
        this.player.load()
      }

      if (payload.command === 'play') {
        this.player.play()
      }
    })
  },
  mounted() {
    const { video } = this.$refs

    this.player = video

    if (this.autoplay) {
      this.playing = true
    }
  },
  methods: {
    changeStatus() {
      this.playing = !this.playing
    },
    showShare() {
      this.displayShareOverlay = !this.displayShareOverlay

      if (this.displayShareOverlay) {
        this.playing = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  .vt-video {
    overflow: hidden;
    width: 100%;
    padding-bottom: calc(100%/1.8);

    video {
      background: #000;
      border: 0;
      outline: none;
      position: absolute;
      left: 0;
      top: 0;
    }

    &__poster {
      display: flex;
      align-items: center;
      justify-content: center;
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      background: #f8f8f8;
      background-position: center center;
      background-repeat: no-repeat;
      background-size: cover;
      z-index: 1;

      & button {
        z-index: 2;
      }

      &--active {
        visibility: hidden;
      }
    }
  }
</style>
